import { ImageVariant, VARIANT_DIMENSIONS, VARIANT_RESPONSIVE_SRCSET } from './contracts';
import { Env } from '@slc/tools/utils/getEnv';

export interface GetImageUrlProps {
  // Widened to `| undefined` (SC-1167 fix): an unresolved value reaching this
  // function must be a compile-time-visible case, not a silent `undefined`
  // that only surfaces once it's already been interpolated into a path.
  src: string | undefined;
  variant?: ImageVariant;
  env: Env;
  // Optional rather than `| undefined`: callers with no tenant imageUrl to
  // hand over (e.g. a fixture with no context.imageUrl) can omit the key
  // entirely — semantically identical, since every read of `imageUrl` below
  // already treats a missing key and an explicit `undefined` the same way.
  imageUrl?: string;
  // Canonical host (origin) for tenant-agnostic club logos that live in the shared
  // master/logos GCS folder. Set only where the per-app imageUrl can't serve /img
  // (e.g. Streamline). When unset, club logos fall through to the imageUrl branch.
  assetsUrl?: string;
}

/**
 * Absolute host that serves shared (tenant-agnostic) `master/…` assets.
 *
 * These live behind the tenant's public `/img` (and `/assets`) host — sourced from
 * `master.tenants[].env[prod|test|local].imageUrl` — so we must emit an ABSOLUTE URL,
 * not a relative `/img/master/…`. A relative URL resolves against whichever app is
 * rendering: the web app's own origin serves `/img`, but the admin app's origin
 * (`admin.<tenant>`) does not, so its preview would 404 (return the SPA shell).
 *
 * We take the origin of `assetsUrl` (the canonical override, e.g. Streamline) or the
 * per-tenant `imageUrl`. Using the origin — not the full `imageUrl` — deliberately drops
 * any per-tenant path segment (e.g. test's `/img/<tenant>`); shared master assets are not
 * tenant-scoped and must be reached at `<host>/img/master/…`. Falls back to the test host
 * (non-prod) or a relative path (prod) only when no absolute URL is configured (e.g. tests).
 */
const getMasterAssetsHost = ({
  imageUrl,
  assetsUrl,
  env,
}: Pick<GetImageUrlProps, 'imageUrl' | 'assetsUrl' | 'env'>) => {
  const configured = assetsUrl || imageUrl;
  if (configured && configured.startsWith('http')) {
    try {
      return new URL(configured).origin;
    } catch {
      // Malformed URL — fall through to the env-based default below.
    }
  }
  return env !== Env.PRODUCTION ? 'https://test.streamlinecontent.tech' : '';
};

/**
 * Matches a path segment that is literally `undefined` or `null` — the footprint
 * of an unresolved value having been interpolated into a path somewhere upstream.
 *
 * Production (SC-1167) saw `/img/undefined/master/logos/<file>` across four
 * tenants, from an unset `ASSETS_DOMAIN` indirection baking `undefined/` into
 * stored club-image paths (fixed at source in 0.129.1; the stored values were
 * repaired by `apps/api/scripts/backfill-club-image-paths.ts`).
 *
 * This guard is deliberately a *detector*, not a repair: we refuse to build the
 * URL rather than string-replacing the bad segment, because silently rewriting it
 * would hide a real upstream defect and could point at the wrong object. It only
 * applies to paths WE construct below (see the ordering note in `getImageUrl`) —
 * an absolute URL passed straight through is never inspected for this shape.
 */
const UNRESOLVED_PATH_SEGMENT = /(?:^|\/)(?:undefined|null)(?:\/|$)/;

/** True when `src` is a non-empty string — required before any `src.startsWith(...)`
 *  call below, which would throw on a non-string `src`. */
const isNonEmptyString = (src: unknown): src is string => typeof src === 'string' && src !== '';

/**
 * Builds an image URL, or `null` when no safe URL could be composed (SC-1167).
 *
 * Callers must treat `null` as "render the placeholder/fallback and issue NO
 * request" — see `Image.tsx`, which omits the `src` attribute entirely rather
 * than emitting `src=""` (browsers resolve an empty src to the document URL and
 * re-request the page).
 */
export const getImageUrl = ({ src, variant, env, imageUrl, assetsUrl }: GetImageUrlProps): string | null => {
  // A missing/empty `src` is a no-op, not a TypeError — an SSR render must not
  // 500 because one image reference is absent. Must run before `src.startsWith`
  // below, which would throw on a non-string `src`.
  if (!isNonEmptyString(src)) {
    return null;
  }

  if (src.startsWith('https') || src.startsWith('blob:') || src.startsWith('data:')) {
    return src;
  }

  // Refuse anything that would interpolate an unresolved value into a path WE
  // build below (`master/`, `teams/`, or the imageUrl branch). Checked AFTER
  // the scheme pass-through above, not before: a legitimate absolute URL that
  // happens to contain a `/null/`-shaped segment (e.g. a third-party CDN path)
  // must be returned verbatim — SC-1167 was specifically about an unresolved
  // value landing in a path we construct ourselves, not one we pass through.
  if (UNRESOLVED_PATH_SEGMENT.test(src)) {
    return null;
  }

  if (src.startsWith('master/')) {
    const baseUrl = getMasterAssetsHost({ imageUrl, assetsUrl, env });
    return variant ? `${baseUrl}/img/${src}?variant=${variant}&fit=contain` : `${baseUrl}/assets/${src}`;
  }

  // Club logos (`teams/<file>`) are tenant-agnostic and served from master/logos via
  // the assets-proxy `/img/teams/` route. When a canonical assets host is configured,
  // route them there instead of the per-app imageUrl (env is unreliable for SPA builds).
  if (assetsUrl && src.startsWith('teams/')) {
    const base = assetsUrl.replace(/\/$/, '');
    return variant ? `${base}/img/${src}?variant=${variant}&fit=contain` : `${base}/img/${src}`;
  }

  // Unlike the `master/` branch (which derives its own base via
  // getMasterAssetsHost) this branch interpolates `imageUrl` directly, so an
  // absent value would stringify to the literal "undefined" and produce exactly
  // the `/img/undefined/…` shape SC-1167 was raised for. An empty string stays
  // valid — it yields a root-relative URL, which several callers rely on.
  if (typeof imageUrl !== 'string') {
    return null;
  }

  const normalizedSrc = src.startsWith('/') ? src.slice(1) : src;
  return variant ? `${imageUrl}/${normalizedSrc}?variant=${variant}&fit=contain` : `${imageUrl}/${normalizedSrc}`;
};

export const getImageSrcSet = (props: GetImageUrlProps): string | undefined => {
  if (!props.variant) return undefined;
  const ladder = VARIANT_RESPONSIVE_SRCSET[props.variant];
  if (!ladder) return undefined;
  const candidates = ladder.map((v) => ({ v, url: getImageUrl({ ...props, variant: v }) }));
  // A srcset is only meaningful if the underlying URL could be built at all;
  // otherwise every candidate would collapse to a bare width descriptor. `null`
  // depends only on `src`/`imageUrl`, never on `variant`, so any one candidate
  // answers it — no need for a separate probe call.
  if (candidates[0]!.url === null) return undefined;
  return candidates.map(({ v, url }) => `${url} ${VARIANT_DIMENSIONS[v]!.width}w`).join(', ');
};
